import { usePrivy } from '@privy-io/react-auth';
import { useAccount, useBalance } from 'wagmi';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Wallet,
  Bell,
  Shield,
  LogOut,
  ExternalLink,
  Copy,
  Check,
  Plus,
  Gamepad2,
  Trash2,
  Loader2,
  CheckCircle,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { formatAddress } from '@/lib/utils';
import { useState, useEffect, useCallback } from 'react';
import {
  api,
  type GameAccount,
  type GameWithVerificationInfo,
  type MatchVerificationSession,
  type ProfileCodeVerificationSession,
  type NotificationPreferences,
} from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import { GameIcon } from '@/components/ui/GameIcon';

export function SettingsPage() {
  const { user, logout, linkWallet } = usePrivy();
  const { address, isConnected } = useAccount();
  const { data: balance } = useBalance({ address });
  const [copied, setCopied] = useState(false);
  const appUser = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  // Profile state
  const [displayName, setDisplayName] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);

  // Notification preferences state
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences | null>(null);
  const [togglingNotif, setTogglingNotif] = useState<string | null>(null);

  // Game accounts state
  const [gameAccounts, setGameAccounts] = useState<GameAccount[]>([]);
  const [availableGames, setAvailableGames] = useState<GameWithVerificationInfo[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [selectedGame, setSelectedGame] = useState('');
  const [gameUserId, setGameUserId] = useState('');
  const [isLinking, setIsLinking] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [unlinkingId, setUnlinkingId] = useState<string | null>(null);

  // Match verification state
  const [verificationSession, setVerificationSession] = useState<MatchVerificationSession | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // Profile code verification state
  const [profileCodeSession, setProfileCodeSession] = useState<ProfileCodeVerificationSession | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [accounts, games, prefs] = await Promise.all([
        api.getGameAccounts(),
        api.getGamesWithVerificationInfo(),
        api.getNotificationPreferences().catch(() => null),
      ]);
      setGameAccounts(accounts);
      setAvailableGames(games.filter(g => g.hasApiKey));
      if (prefs) setNotifPrefs(prefs);
    } catch (err) {
      console.error('Failed to fetch game data:', err);
    } finally {
      setIsLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Initialize display name from auth store
  useEffect(() => {
    if (appUser?.displayName) {
      setDisplayName(appUser.displayName);
    }
  }, [appUser?.displayName]);

  const handleSaveProfile = async () => {
    if (!displayName.trim() || displayName === appUser?.displayName) return;
    setIsSavingProfile(true);
    setProfileSaved(false);
    try {
      const updatedUser = await api.updateProfile({ displayName: displayName.trim() });
      setUser(updatedUser);
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 3000);
    } catch (err) {
      console.error('Failed to update profile:', err);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleToggleNotif = async (key: 'wagerUpdates' | 'challenges' | 'securityAlerts') => {
    if (!notifPrefs) return;
    setTogglingNotif(key);
    try {
      const current = notifPrefs[key];
      const updated = current.length > 0 ? [] : ['in_app', 'push', 'email'];
      const newPrefs = await api.updateNotificationPreferences({ [key]: updated });
      setNotifPrefs(newPrefs);
    } catch (err) {
      console.error('Failed to update notification preferences:', err);
    } finally {
      setTogglingNotif(null);
    }
  };

  const handleCopyAddress = () => {
    if (address) {
      navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const selectedGameInfo = availableGames.find(g => g.gameCode === selectedGame);

  const handleOAuthLink = async (game: GameWithVerificationInfo) => {
    if (!game.oauthProvider) return;
    setIsLinking(true);
    setLinkError('');
    try {
      const authUrl = await api.startOAuth(game.oauthProvider, game.oauthGameParam);
      window.location.href = authUrl;
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to start OAuth');
      setIsLinking(false);
    }
  };

  const handleLinkAccount = async () => {
    if (!selectedGame || !gameUserId.trim()) return;

    setIsLinking(true);
    setLinkError('');
    try {
      await api.linkGameAccount(selectedGame, gameUserId.trim(), gameUserId.trim());
      await fetchData();
      setShowLinkForm(false);
      setSelectedGame('');
      setGameUserId('');
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to link account');
    } finally {
      setIsLinking(false);
    }
  };

  const handleInitiateMatchVerification = async () => {
    if (!selectedGame || !gameUserId.trim()) return;

    setIsLinking(true);
    setLinkError('');
    try {
      const session = await api.initiateMatchVerification(selectedGame, gameUserId.trim());
      setVerificationSession(session);
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to find account');
    } finally {
      setIsLinking(false);
    }
  };

  const handleConfirmMatchVerification = async () => {
    if (!verificationSession) return;

    setIsVerifying(true);
    setLinkError('');
    try {
      await api.confirmMatchVerification(verificationSession.id);
      await fetchData();
      setVerificationSession(null);
      setShowLinkForm(false);
      setSelectedGame('');
      setGameUserId('');
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleInitiateProfileCodeVerification = async () => {
    if (!selectedGame || !gameUserId.trim()) return;

    setIsLinking(true);
    setLinkError('');
    try {
      const session = await api.initiateProfileCodeVerification(selectedGame, gameUserId.trim());
      setProfileCodeSession(session);
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to find account');
    } finally {
      setIsLinking(false);
    }
  };

  const handleConfirmProfileCodeVerification = async () => {
    if (!profileCodeSession) return;

    setIsVerifying(true);
    setLinkError('');
    try {
      await api.confirmProfileCodeVerification(profileCodeSession.id);
      await fetchData();
      setProfileCodeSession(null);
      setShowLinkForm(false);
      setSelectedGame('');
      setGameUserId('');
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleUnlinkAccount = async (accountId: string) => {
    setUnlinkingId(accountId);
    try {
      await api.unlinkGameAccount(accountId);
      await fetchData();
    } catch (err) {
      console.error('Failed to unlink account:', err);
    } finally {
      setUnlinkingId(null);
    }
  };

  // Games the user hasn't linked yet
  const unlinkedGames = availableGames.filter(
    g => !gameAccounts.some(a => a.gameCode === g.gameCode)
  );

  return (
    <div className="mx-auto max-w-2xl space-y-6 pb-20 lg:pb-0">
      <h1 className="font-heading text-2xl font-bold">Settings</h1>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={appUser?.avatarUrl || (user?.google as { picture?: string } | undefined)?.picture} />
              <AvatarFallback>
                {(appUser?.displayName || user?.email?.address)?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Display Name</label>
            <div className="flex gap-2">
              <Input
                placeholder="Your display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveProfile()}
              />
              <Button
                onClick={handleSaveProfile}
                disabled={isSavingProfile || !displayName.trim() || displayName === appUser?.displayName}
                size="sm"
                className="shrink-0"
              >
                {isSavingProfile ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : profileSaved ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  'Save'
                )}
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Email</label>
            <Input disabled value={user?.email?.address || ''} />
          </div>
        </CardContent>
      </Card>

      {/* Game Accounts */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gamepad2 className="h-5 w-5" />
            Game Accounts
          </CardTitle>
          <CardDescription>
            Link your game accounts to create and accept challenges
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingAccounts ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {/* Linked accounts */}
              <AnimatePresence>
                {gameAccounts.map((account) => (
                  <motion.div
                    key={account.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-3">
                      <GameIcon gameCode={account.gameCode} size="sm" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{account.gameUsername || account.username}</p>
                          {account.verified ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-yellow-500" />
                          )}
                        </div>
                        {account.rank && (
                          <p className="text-xs font-medium text-amber-500">{account.rank}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {availableGames.find(g => g.gameCode === account.gameCode)?.displayName || account.gameCode}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {account.profileUrl && (
                        <Button variant="ghost" size="icon" asChild>
                          <a href={account.profileUrl} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleUnlinkAccount(account.id)}
                        disabled={unlinkingId === account.id}
                      >
                        {unlinkingId === account.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-destructive" />
                        )}
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {gameAccounts.length === 0 && !showLinkForm && (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  No game accounts linked yet. Link one to start competing!
                </p>
              )}

              {/* Link form */}
              <AnimatePresence>
                {showLinkForm && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-3 overflow-hidden rounded-lg border bg-muted/30 p-4"
                  >
                    {linkError && (
                      <p className="text-sm text-destructive">{linkError}</p>
                    )}

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Select Game</label>
                      <div className="grid grid-cols-2 gap-2">
                        {unlinkedGames.map((game) => (
                          <button
                            key={game.gameCode}
                            disabled={isLinking}
                            onClick={() => {
                              if (game.verificationMethod === 'oauth') {
                                handleOAuthLink(game);
                              } else {
                                setSelectedGame(game.gameCode);
                                setLinkError('');
                                setGameUserId('');
                                setVerificationSession(null);
                                setProfileCodeSession(null);
                              }
                            }}
                            className={`flex items-center gap-2 rounded-lg border p-2.5 text-left text-sm transition-colors hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed ${
                              selectedGame === game.gameCode
                                ? 'border-primary bg-primary/10'
                                : 'border-border'
                            }`}
                          >
                            <GameIcon gameCode={game.gameCode} size="sm" />
                            <span className="flex-1 truncate">{game.displayName}</span>
                            {game.verificationMethod === 'oauth' && !isLinking && (
                              <ExternalLink className="h-3 w-3 text-muted-foreground" />
                            )}
                            {game.verificationMethod === 'oauth' && isLinking && (
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Input form for non-OAuth games */}
                    {selectedGame && selectedGameInfo && !verificationSession && !profileCodeSession && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-3"
                      >
                        <div className="space-y-2">
                          <label className="text-sm font-medium">
                            {selectedGameInfo.inputLabel || 'Game Username / ID'}
                          </label>
                          <Input
                            autoFocus
                            placeholder={selectedGameInfo.inputPlaceholder || 'Your in-game username or ID'}
                            value={gameUserId}
                            onChange={(e) => {
                              setGameUserId(e.target.value);
                              setLinkError('');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                if (selectedGameInfo.verificationMethod === 'match_verification') {
                                  handleInitiateMatchVerification();
                                } else if (selectedGameInfo.verificationMethod === 'in_game_code') {
                                  handleInitiateProfileCodeVerification();
                                } else {
                                  handleLinkAccount();
                                }
                              }
                            }}
                          />
                        </div>

                        <div className="flex gap-2">
                          <Button
                            onClick={
                              selectedGameInfo.verificationMethod === 'match_verification'
                                ? handleInitiateMatchVerification
                                : selectedGameInfo.verificationMethod === 'in_game_code'
                                  ? handleInitiateProfileCodeVerification
                                  : handleLinkAccount
                            }
                            disabled={!gameUserId.trim() || isLinking}
                            className="flex-1"
                          >
                            {isLinking ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : selectedGameInfo.verificationMethod === 'match_verification' || selectedGameInfo.verificationMethod === 'in_game_code' ? (
                              <Gamepad2 className="mr-2 h-4 w-4" />
                            ) : (
                              <Check className="mr-2 h-4 w-4" />
                            )}
                            {selectedGameInfo.verificationMethod === 'match_verification' || selectedGameInfo.verificationMethod === 'in_game_code'
                              ? 'Find Account'
                              : 'Link Account'}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowLinkForm(false);
                              setSelectedGame('');
                              setGameUserId('');
                              setLinkError('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </motion.div>
                    )}

                    {/* Match verification step 2: play and confirm */}
                    {verificationSession && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-3"
                      >
                        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-3">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-amber-500" />
                            <p className="text-sm font-semibold">
                              Account found: {verificationSession.gameUsername}
                            </p>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Play any match on {selectedGameInfo?.displayName}, then click Verify.
                            Battle logs can take 2-10 minutes to update.
                          </p>
                          {linkError && (
                            <p className="text-sm text-destructive">{linkError}</p>
                          )}
                          <div className="flex gap-2">
                            <Button
                              onClick={handleConfirmMatchVerification}
                              disabled={isVerifying}
                              className="flex-1"
                            >
                              {isVerifying ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <CheckCircle className="mr-2 h-4 w-4" />
                              )}
                              Verify
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setVerificationSession(null);
                                setSelectedGame('');
                                setGameUserId('');
                                setLinkError('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Profile code verification step 2: set code in profile */}
                    {profileCodeSession && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-3"
                      >
                        <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 space-y-3">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4 text-blue-500" />
                            <p className="text-sm font-semibold">
                              Account found: {profileCodeSession.gameUsername}
                            </p>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Set your {selectedGameInfo?.displayName} <strong>{profileCodeSession.profileField}</strong> to:
                          </p>
                          <div className="flex items-center justify-center rounded-md bg-background border p-2">
                            <code className="text-base font-bold tracking-widest text-primary">{profileCodeSession.code}</code>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Go to your profile settings, set your Location to the code above, then click Verify. You can change it back after.
                          </p>
                          {linkError && (
                            <p className="text-sm text-destructive">{linkError}</p>
                          )}
                          <div className="flex gap-2">
                            <Button
                              onClick={handleConfirmProfileCodeVerification}
                              disabled={isVerifying}
                              className="flex-1"
                            >
                              {isVerifying ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <CheckCircle className="mr-2 h-4 w-4" />
                              )}
                              Verify
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => {
                                setProfileCodeSession(null);
                                setSelectedGame('');
                                setGameUserId('');
                                setLinkError('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Link button */}
              {!showLinkForm && unlinkedGames.length > 0 && (
                <Button
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => setShowLinkForm(true)}
                >
                  <Plus className="h-4 w-4" />
                  Link Game Account
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Wallet */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Wallet
          </CardTitle>
          <CardDescription>
            Connect your wallet to fund challenges and collect winnings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isConnected && address ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border p-4"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-lg font-medium">{formatAddress(address)}</p>
                  <p className="text-sm text-muted-foreground">
                    {balance?.formatted} {balance?.symbol}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={handleCopyAddress}>
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                  <Button variant="ghost" size="icon" asChild>
                    <a
                      href={`https://basescan.org/address/${address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                </div>
              </div>
              <Badge variant="success" className="mt-3">
                Connected to Base
              </Badge>
            </motion.div>
          ) : (
            <Button variant="gradient" className="w-full gap-2" onClick={linkWallet}>
              <Plus className="h-4 w-4" />
              Connect Wallet
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {([
            { key: 'wagerUpdates' as const, label: 'Challenge Updates', description: 'When your challenges are accepted or settled' },
            { key: 'challenges' as const, label: 'Challenge Alerts', description: 'When someone challenges you' },
            { key: 'securityAlerts' as const, label: 'Security Alerts', description: 'Login attempts and account changes' },
          ]).map((item) => {
            const enabled = notifPrefs ? notifPrefs[item.key].length > 0 : true;
            return (
              <div key={item.key} className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{item.label}</p>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
                <Button
                  variant={enabled ? 'outline' : 'ghost'}
                  size="sm"
                  disabled={togglingNotif === item.key}
                  onClick={() => handleToggleNotif(item.key)}
                >
                  {togglingNotif === item.key ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : null}
                  {enabled ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Two-Factor Authentication</p>
              <p className="text-sm text-muted-foreground">Add an extra layer of security</p>
            </div>
            <Badge variant="secondary">Coming Soon</Badge>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Active Sessions</p>
              <p className="text-sm text-muted-foreground">Manage your active sessions</p>
            </div>
            <Button variant="outline" size="sm">
              View
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Log Out</p>
              <p className="text-sm text-muted-foreground">Sign out of your account</p>
            </div>
            <Button variant="destructive" size="sm" onClick={logout}>
              <LogOut className="mr-2 h-4 w-4" />
              Log Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
